import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useApi } from "../../hooks/useApi";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Drawer,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ListIcon from "@mui/icons-material/List";
import PersonIcon from "@mui/icons-material/Person";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import NotesIcon from "@mui/icons-material/StickyNote2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PaymentsIcon from "@mui/icons-material/Payments";
import BlockIcon from "@mui/icons-material/Block";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import NewBookingDrawer from "./NewBookingDrawer";
import BlockDrawer from "./BlockDrawer";
import { useTenant } from "../../hooks/useTenant";
import {
  HOUR_START,
  HOUR_END,
  TOTAL_HOURS,
  SLOT_MINUTES,
  PX_PER_MIN,
  PX_PER_HOUR,
  TOTAL_HEIGHT,
  GUTTER_WIDTH,
  SNAP_MINUTES,
  BARBER_COLORS,
  STATUS_STYLES,
  dateToPx,
  pxToDate,
} from "./calendarGridShared";

import { formatMonthYear, formatPhoneNumber } from "../../utils/functions";

dayjs.extend(utc);
dayjs.extend(timezone);

// Shared engine (constants, colors, geometry) imported at the top of the file —
// the same module powers the desktop grid.

// ═══════════════════════════════════════════
// DRAGGABLE EVENT BLOCK
// ═══════════════════════════════════════════

const EventBlock = ({
  booking,
  columnWidth,
  onDragStart,
  onResizeStart,
  onTap,
  isDragging,
  isResizing,
  dragY,
  dragColIndex,
  colIndex,
  liveDragStart,
  stackIndex = 0,
  stackTotal = 1,
}) => {
  const top = dateToPx(booking.starts_at);
  const durationMs = booking.ends_at - booking.starts_at;
  const durationMin = durationMs / 60000;
  const height = Math.max(durationMin * PX_PER_MIN, 28);
  const color = booking.barber.color;
  //const isTiny = durationMin <= 15;
  const isShort = durationMin <= 20;
  const isCancelled = booking.status === "cancelled";
  const canDrag = !["completed", "cancelled", "no_show"].includes(
    booking.status,
  );

  const statusDot =
    {
      pending: "#EF9F27",
      confirmed: "#378ADD",
      completed: "#1D9E75",
      cancelled: "#E24B4A",
      no_show: "#9b9b9b",
    }[booking.status] || "#9b9b9b";
  const isThisDragging = isDragging && booking.id === isDragging;

  const displayStart = liveDragStart || booking.starts_at;
  const displayEnd = liveDragStart
    ? new Date(liveDragStart.getTime() + durationMs)
    : booking.ends_at;

  const [dragActive, setDragActive] = useState(false);
  const [resizeActive, setResizeActive] = useState(false);

  // Reset visual state when parent confirms the operation ended
  useEffect(() => {
    if (!isDragging) setDragActive(false);
  }, [isDragging]);
  useEffect(() => {
    if (!isResizing) setResizeActive(false);
  }, [isResizing]);

  const longPressTimer = useRef(null);
  const didDrag = useRef(false);
  const resizeTimer = useRef(null);
  const didResize = useRef(false);

  const handleDragHandleTouchStart = (e) => {
    if (isResizing) return;
    e.stopPropagation();
    didDrag.current = false;
    const touch = e.touches[0];
    longPressTimer.current = setTimeout(() => {
      didDrag.current = true;
      setDragActive(true);
      if (navigator.vibrate) navigator.vibrate(40);
      onDragStart(booking.id, touch.clientY, touch.clientX, colIndex, top);
    }, 350);
  };

  const handleDragHandleTouchMove = (e) => {
    if (didDrag.current) return; // drag active — let window listener handle it
    e.stopPropagation();
    clearTimeout(longPressTimer.current);
  };

  const handleDragHandleTouchEnd = (e) => {
    if (didDrag.current) {
      didDrag.current = false;
      return; // drag active — let window listener handle the drop
    }
    e.stopPropagation();
    clearTimeout(longPressTimer.current);
  };

  const handleClick = () => {
    if (!didDrag.current) {
      onTap(booking);
    }
    didDrag.current = false;
  };

  const snappedDragY = isThisDragging
    ? Math.round((dragY ?? top) / (SNAP_MINUTES * PX_PER_MIN)) *
      (SNAP_MINUTES * PX_PER_MIN)
    : top;

  const resolvedTop = isThisDragging ? snappedDragY : top;
  const isHidden = isThisDragging && dragColIndex !== colIndex;

  // Stack offset — cancelled always gets lowest stackIndex so it's behind
  const STACK_OFFSET = 10;
  const stackLeft = stackTotal > 1 ? 2 + stackIndex * STACK_OFFSET : 2;
  const stackRight =
    stackTotal > 1 ? 2 + (stackTotal - 1 - stackIndex) * STACK_OFFSET : 2;

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: "absolute",
        top: resolvedTop,
        left: isHidden ? -9999 : stackLeft,
        right: isHidden ? 9999 : stackRight,
        height,
        backgroundColor: isCancelled ? "#eeece8" : color.bg,
        borderLeft: `3px solid ${isCancelled ? "#c4c0b8" : color.border}`,
        borderRadius: "4px",
        px: "6px",
        py: "3px",
        overflow: "hidden",
        cursor: "pointer",
        touchAction: "pan-x pan-y",
        opacity: isThisDragging
          ? dragActive
            ? 0.65
            : 0.35
          : isCancelled
            ? 0.75
            : 1,
        transition: isThisDragging ? "none" : "opacity 0.15s",
        userSelect: "none",
        WebkitUserSelect: "none",
        zIndex: isThisDragging ? 0 : stackIndex + 1,
        boxShadow: isCancelled ? "none" : "0 1px 3px rgba(0,0,0,0.08)",
        color: isCancelled ? "#9b9b9b" : color.text,
      }}
    >
      {isShort ? (
        // ── Short (≤25 min): time row, then name + service inline ──
        <Box
          sx={{
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            px: "2px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                backgroundColor: statusDot,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: 12,
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {dayjs(displayStart).format("h:mm A")} –{" "}
              {dayjs(displayEnd).format("h:mm A")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row", gap: "5px" }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: 12,
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {booking.client.first_name} {booking.client.last_name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: 12,
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {booking.service.name}
            </Typography>
          </Box>
        </Box>
      ) : (
        // ── Full (>25 min): time, name, service stacked ──
        <Box
          sx={{
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            px: "2px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                backgroundColor: statusDot,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: 12,
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {dayjs(displayStart).format("h:mm A")} –{" "}
              {dayjs(displayEnd).format("h:mm A")}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {booking.client.first_name} {booking.client.last_name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {booking.service.name}
          </Typography>
        </Box>
      )}

      {/* Drag handle — hold here to drag the booking */}
      {canDrag && (
        <Box
          onTouchStart={handleDragHandleTouchStart}
          onTouchMove={handleDragHandleTouchMove}
          onTouchEnd={handleDragHandleTouchEnd}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "grab",
            touchAction: "none",
            zIndex: 3,
            opacity: dragActive ? 1 : 0.45,
            transition: "opacity 0.15s",
          }}
        >
          <DragIndicatorIcon
            sx={{ fontSize: 13, color: dragActive ? "#4caf50" : "inherit" }}
          />
        </Box>
      )}

      {/* Resize handle — hold here to extend or shorten the booking */}
      {canDrag && height >= 28 && (
        <Box
          onTouchStart={(e) => {
            if (isDragging) return;
            e.stopPropagation();
            didResize.current = false;
            clearTimeout(resizeTimer.current);
            const touch = e.touches[0];
            resizeTimer.current = setTimeout(() => {
              didResize.current = true;
              setResizeActive(true);
              if (navigator.vibrate) navigator.vibrate(40);
              onResizeStart(booking.id, touch.clientY, booking.ends_at);
            }, 350);
          }}
          onTouchMove={(e) => {
            if (didResize.current) return; // resize active — let window listener handle it
            e.stopPropagation();
            clearTimeout(resizeTimer.current);
          }}
          onTouchEnd={(e) => {
            if (didResize.current) {
              didResize.current = false;
              return; // resize active — let window listener handle the drop
            }
            e.stopPropagation();
            clearTimeout(resizeTimer.current);
          }}
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "ns-resize",
            touchAction: "none",
            zIndex: 3,
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 3,
              borderRadius: 1.5,
              backgroundColor: resizeActive ? "#4caf50" : "rgba(0,0,0,0.2)",
              transition: "background-color 0.15s",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════
// DRAG GHOST — floats on top of everything
// ═══════════════════════════════════════════

const DragGhost = ({ booking, x, y, columnWidth, liveDragStart }) => {
  if (!booking) return null;
  const durationMs = booking.ends_at - booking.starts_at;
  const durationMin = durationMs / 60000;
  const height = Math.max(durationMin * PX_PER_MIN, 28);
  const color = booking.barber.color;

  const displayStart = liveDragStart || booking.starts_at;
  const displayEnd = liveDragStart
    ? new Date(liveDragStart.getTime() + durationMs)
    : booking.ends_at;

  return (
    <Box
      sx={{
        position: "fixed",
        left: x - columnWidth / 2,
        top: y - height / 2,
        width: columnWidth - 4,
        height,
        backgroundColor: color.bg,
        borderLeft: `3px solid ${color.border}`,
        borderRadius: "4px",
        px: "6px",
        py: "3px",
        overflow: "hidden",
        opacity: 0.92,
        zIndex: 9999,
        pointerEvents: "none",
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        transform: "scale(1.04)",
      }}
    >
      <Typography
        sx={{
          fontSize: 9,
          fontWeight: 600,
          opacity: 0.75,
          lineHeight: 1.3,
          color: color.text,
        }}
      >
        {dayjs(displayStart).format("h:mm A")} –{" "}
        {dayjs(displayEnd).format("h:mm A")}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1.3,
          color: color.text,
        }}
      >
        {booking.client.first_name} {booking.client.last_name}
      </Typography>

      {/* Green drag indicator — confirms drag is active */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 13, color: "#4caf50" }} />
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════
// TIME GUTTER
// ═══════════════════════════════════════════

const TimeGutter = () => {
  const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => HOUR_START + i);
  return (
    <Box
      sx={{
        width: GUTTER_WIDTH,
        flexShrink: 0,
        position: "relative",
        height: TOTAL_HEIGHT,
        borderRight: "1px solid #f0f0f0",
        backgroundColor: "#fafafa",
      }}
    >
      {hours.map((h) => (
        <Box
          key={h}
          sx={{
            position: "absolute",
            top: (h - HOUR_START) * PX_PER_HOUR - 8,
            right: 8,
            width: GUTTER_WIDTH - 8,
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              color: "#9b9b9b",
              fontWeight: 500,
              textAlign: "right",
            }}
          >
            {dayjs().hour(h).minute(0).format("h A")}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ═══════════════════════════════════════════
// GRID LINES
// ═══════════════════════════════════════════

const GridLines = () => {
  const slots = Array.from(
    { length: TOTAL_HOURS * (60 / SLOT_MINUTES) },
    (_, i) => i,
  );
  return (
    <>
      {slots.map((i) => {
        const minutes = i * SLOT_MINUTES;
        const isHour = minutes % 60 === 0;
        return (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: minutes * PX_PER_MIN,
              left: 0,
              right: 0,
              height: "1px",
              backgroundColor: isHour ? "#ebebeb" : "#f5f5f5",
              borderTop: isHour ? "none" : "1px dashed #f0f0f0",
              pointerEvents: "none",
            }}
          />
        );
      })}
    </>
  );
};

// ═══════════════════════════════════════════
// NOW INDICATOR
// ═══════════════════════════════════════════

const NowIndicator = ({ currentDate }) => {
  const now = new Date();
  const isToday = dayjs(currentDate).isSame(now, "day");
  if (!isToday) return null;
  // Hide when the current time is outside the visible grid window. Otherwise the
  // absolutely-positioned line sits above/below the grid (e.g. at 11 PM it lands
  // 2h past a 9 PM end), extending the scroll area into blank space.
  const hourOfDay = now.getHours() + now.getMinutes() / 60;
  if (hourOfDay < HOUR_START || hourOfDay >= HOUR_END) return null;
  const top = dateToPx(now);
  return (
    <Box
      sx={{
        position: "absolute",
        top,
        left: 0,
        right: 0,
        zIndex: 5,
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: -4,
          top: -4,
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: "#E24B4A",
        }}
      />
      <Box sx={{ height: 2, backgroundColor: "#E24B4A" }} />
    </Box>
  );
};

// ═══════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════

export default function MobileBookingCalendar({ lockedBarber = null }) {
  const api = useApi();
  const { tenant } = useTenant();
  const tzName = tenant?.timezone || "America/New_York";

  // Bookings are parsed from shop-local wall-clock strings into Dates using the
  // BROWSER timezone, so a Date's wall-clock components equal the shop wall-clock
  // the user sees. Re-interpret those components in the SHOP timezone (not the
  // browser's) when sending UTC back — dayjs(date).utc() would shift the time
  // whenever the staff member's device tz differs from the shop's.
  const toShopUtc = useCallback(
    (date) =>
      dayjs
        .tz(dayjs(date).format("YYYY-MM-DD HH:mm:ss"), tzName)
        .utc()
        .format("YYYY-MM-DD HH:mm:ss"),
    [tzName],
  );

  const [barbers, setBarbers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [barberHours, setBarberHours] = useState({});
  const [dateOverrides, setDateOverrides] = useState({});
  const [selectedBarberIds, setSelectedBarberIds] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [newBookingPrefill, setNewBookingPrefill] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [drawerMessage, setDrawerMessage] = useState(null);
  const [statusLoading, setStatusLoading] = useState(null);

  const [weekCounts, setWeekCounts] = useState({});

  // Block management
  const [blockEvents, setBlockEvents] = useState([]);
  const [blockDrawerOpen, setBlockDrawerOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [deletingBlock, setDeletingBlock] = useState(false);

  // ── Drag state ───────────────────────────────────────────────────────
  const [drag, setDrag] = useState(null);
  // drag = { bookingId, ghostX, ghostY, startTouchY, startTouchX,
  //          startTop, startColIndex, currentColIndex, currentTop }

  // ── Resize state ─────────────────────────────────────────────────────
  const [resizingId, setResizingId] = useState(null);
  const resizeRef = useRef(null);
  // resizeRef = { bookingId, startClientY, originalEndsAt, currentEndsAt }

  const gridRef = useRef(null);
  const columnRefs = useRef([]);
  const animFrameRef = useRef(null);
  const bookingsRef = useRef(bookings);

  // Keep bookingsRef current so touch handlers always see latest bookings
  useEffect(() => {
    bookingsRef.current = bookings;
  }, [bookings]);

  // Barber header is now inside the grid scroll container — no JS sync needed.

  // ── Fetch barbers ────────────────────────────────────────────────────
  const fetchBarbers = useCallback(async () => {
    if (lockedBarber) {
      setBarbers([{ ...lockedBarber, color: BARBER_COLORS[0] }]);
      return;
    }
    try {
      const response = await api("/barbers/barbers.php", { method: "GET" });
      const list = (response.data.barbers || []).map((b, i) => ({
        ...b,
        color: BARBER_COLORS[i % BARBER_COLORS.length],
      }));
      setBarbers(list);
    } catch (err) {
      showToast("Failed to load barbers", "error");
    }
  }, [lockedBarber]);

  // ── Fetch bookings ───────────────────────────────────────────────────
  const fetchBookings = useCallback(async () => {
    try {
      const dateStr = dayjs(currentDate).format("YYYY-MM-DD");
      const params = new URLSearchParams({
        date_from: dateStr,
        date_to: dateStr,
      });

      if (selectedBarberIds.length > 0) {
        params.set("barber_ids", selectedBarberIds.join(","));
      }

      const response = await api(`/tenants/appointments.php?${params}`, {
        method: "GET",
      });

      const mapped = (response.data.bookings || []).map((b) => ({
        id: b.id,
        status: b.status,
        starts_at: new Date(b.starts_at_local.replace(" ", "T")),
        ends_at: new Date(b.ends_at_local.replace(" ", "T")),
        price_charged: b.price_charged,
        payment_method: b.payment_method,
        payment_status: b.payment_status,
        amount_paid: b.amount_paid,
        notes: b.notes,
        client: b.client,
        barber: {
          ...b.barber,
          color:
            barbers.find((x) => String(x.user_id) === String(b.barber.user_id))
              ?.color || BARBER_COLORS[0],
        },
        service: b.service,
      }));

      setBookings(mapped);
      return mapped; // ✅ return so callers can refresh selectedBooking
    } catch (err) {
      showToast("Failed to load bookings", "error");
      return null;
    }
  }, [currentDate, selectedBarberIds, barbers]);

  // ── Derived — must be before fetchBlocks / fetchDateOverrides ────────
  const visibleBarbers = useMemo(
    () =>
      selectedBarberIds.length === 0
        ? barbers
        : barbers.filter((b) => selectedBarberIds.includes(b.user_id)),
    [barbers, selectedBarberIds],
  );

  // ── Fetch date overrides for the current day (single batch call) ─────
  const fetchDateOverrides = useCallback(
    async (barberList) => {
      if (!barberList.length) return;
      const dateStr = dayjs(currentDate).format("YYYY-MM-DD");
      try {
        const ids = barberList.map((b) => b.user_id).join(",");
        const res = await api(`/barbers/date-overrides.php?barber_ids=${ids}`, {
          method: "GET",
        });
        const overridesByBarber = res.data.overrides_by_barber || {};
        const map = {};
        barberList.forEach((b) => {
          const all = overridesByBarber[b.user_id] || [];
          map[b.user_id] = all.find((o) => o.date === dateStr) || null;
        });
        setDateOverrides(map);
      } catch {
        // silently fail
      }
    },
    [currentDate],
  );

  // ── Fetch blocks for the current date (single batch call) ───────────
  const fetchBlocks = useCallback(async () => {
    if (!visibleBarbers.length) return;
    const dateStr = dayjs(currentDate).format("YYYY-MM-DD");
    const dayOfWeek = dayjs(currentDate).day();
    const dayStart = dayjs(currentDate).startOf("day").toDate();
    const dayEnd = dayjs(currentDate).endOf("day").toDate();

    try {
      const ids = visibleBarbers.map((b) => b.user_id).join(",");
      const res = await api(`/barbers/blocks.php?barber_ids=${ids}`, {
        method: "GET",
      });
      const blocksByBarber = res.data.blocks_by_barber || {};
      const allEvents = [];

      for (const barber of visibleBarbers) {
        const allBlocks = blocksByBarber[barber.user_id] || [];
        for (const block of allBlocks) {
          if (!block.is_recurring) {
            const bs = new Date(block.starts_at.replace(" ", "T") + "Z");
            const be = new Date(block.ends_at.replace(" ", "T") + "Z");
            if (bs < dayEnd && be > dayStart) {
              allEvents.push({
                id: `block-${block.id}`,
                starts_at: bs,
                ends_at: be,
                barber_user_id: barber.user_id,
                isBlock: true,
                blockData: block,
                barber,
              });
            }
          } else {
            const matchesDay =
              block.day_of_week === null || block.day_of_week === dayOfWeek;
            const afterStart =
              !block.recurring_start_date ||
              dateStr >= block.recurring_start_date;
            const beforeEnd =
              !block.recurring_end_date || dateStr <= block.recurring_end_date;
            if (matchesDay && afterStart && beforeEnd) {
              const [sh, sm] = block.start_time.split(":").map(Number);
              const [eh, em] = block.end_time.split(":").map(Number);
              const bs = new Date(currentDate);
              bs.setHours(sh, sm, 0, 0);
              const be = new Date(currentDate);
              be.setHours(eh, em, 0, 0);
              allEvents.push({
                id: `block-${block.id}-${dateStr}`,
                starts_at: bs,
                ends_at: be,
                barber_user_id: barber.user_id,
                isBlock: true,
                blockData: block,
                barber,
              });
            }
          }
        }
      }
      setBlockEvents(allEvents);
    } catch {
      // silently fail
    }
  }, [currentDate, visibleBarbers, api]);

  useEffect(() => {
    fetchBarbers();
  }, []);

  useEffect(() => {
    if (barbers.length > 0) {
      fetchBookings();
      fetchBarberHours(barbers);
      fetchDateOverrides(barbers);
      fetchBlocks();
    }
  }, [currentDate, selectedBarberIds, barbers]);

  useEffect(() => {
    if (!selectedBooking) return;
    const updated = bookings.find((b) => b.id === selectedBooking.id);
    if (updated) setSelectedBooking(updated);
  }, [bookings]);

  // ── Fetch barber hours (single batch call) ───────────────────────────
  const fetchBarberHours = useCallback(async (barberList) => {
    if (!barberList.length) return;
    try {
      const ids = barberList.map((b) => b.user_id).join(",");
      const res = await api(`/barbers/schedule.php?barber_ids=${ids}`, {
        method: "GET",
      });
      const hoursByBarber = res.data.hours_by_barber || {};
      const map = {};
      barberList.forEach((b) => {
        map[b.user_id] = hoursByBarber[b.user_id] || [];
      });
      setBarberHours(map);
    } catch (err) {
      console.error("Failed to load barber hours", err);
    }
  }, []);

  // ── Fetch appointment counts for every day in the current week ───────
  const fetchWeekCounts = useCallback(async () => {
    const wStart = dayjs(currentDate).startOf("week").format("YYYY-MM-DD");
    const wEnd = dayjs(currentDate).endOf("week").format("YYYY-MM-DD");
    try {
      const res = await api(
        `/tenants/appointments.php?date_from=${wStart}&date_to=${wEnd}`,
        { method: "GET" },
      );
      const counts = {};
      (res.data.bookings || [])
        .filter((b) => b.status !== "cancelled")
        .forEach((b) => {
          const d = (b.starts_at_local || b.starts_at).slice(0, 10);
          counts[d] = (counts[d] || 0) + 1;
        });
      setWeekCounts(counts);
    } catch {
      // silently fail
    }
  }, [currentDate, api]);

  const weekStartStr = dayjs(currentDate).startOf("week").format("YYYY-MM-DD");
  useEffect(() => {
    fetchWeekCounts();
  }, [weekStartStr]);

  // ── Derived ──────────────────────────────────────────────────────────
  // weekCounts replaces appointmentsByDay — now shows all 7 days correctly

  const weekStart = dayjs(currentDate).startOf("week");
  const weekDays = Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day"));

  const draggingBooking = useMemo(() => {
    if (!drag) return null;
    return bookings.find((b) => b.id === drag.bookingId) || null;
  }, [drag, bookings]);

  // ── Drag handlers ────────────────────────────────────────────────────

  const handleDragStart = useCallback(
    (bookingId, touchClientY, touchClientX, colIndex, eventTop) => {
      const grid = gridRef.current;
      let touchOffsetWithinEvent = 0;
      if (grid) {
        const rect = grid.getBoundingClientRect();
        const fingerPosInGrid = touchClientY - rect.top + grid.scrollTop;
        touchOffsetWithinEvent = Math.max(
          0,
          Math.min(fingerPosInGrid - eventTop, 60),
        );
      }
      setDrag({
        bookingId,
        ghostX: touchClientX,
        ghostY: touchClientY,
        startTouchY: touchClientY,
        startEventTop: eventTop,
        currentTop: eventTop,
        startColIndex: colIndex,
        currentColIndex: colIndex,
        touchOffsetWithinEvent,
      });
    },
    [],
  );

  const getColumnIndexAtX = useCallback((clientX) => {
    for (let i = 0; i < columnRefs.current.length; i++) {
      const el = columnRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) return i;
    }
    return null;
  }, []);

  const getTopAtY = useCallback((clientY) => {
    const grid = gridRef.current;
    if (!grid) return 0;
    const rect = grid.getBoundingClientRect();
    const relY = clientY - rect.top + grid.scrollTop;
    return Math.max(0, Math.min(relY, TOTAL_HEIGHT));
  }, []);

  // Global touch move — attached to window so dragging outside column still works
  useEffect(() => {
    if (!drag) return;

    const handleTouchMove = (e) => {
      e.preventDefault();
      const touch = e.touches[0];

      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = requestAnimationFrame(() => {
        const grid = gridRef.current;
        const colIndex = getColumnIndexAtX(touch.clientX);

        // Compute position from absolute grid coordinates on every frame.
        // This correctly accounts for scrollTop changes from auto-scroll,
        // which the old deltaY approach did not.
        let newTop = 0;
        if (grid) {
          const rect = grid.getBoundingClientRect();
          newTop =
            touch.clientY -
            rect.top +
            grid.scrollTop -
            (drag.touchOffsetWithinEvent || 0);
        } else {
          newTop = drag.startEventTop + (touch.clientY - drag.startTouchY);
        }

        setDrag((prev) => ({
          ...prev,
          ghostX: touch.clientX,
          ghostY: touch.clientY,
          currentTop: Math.max(0, Math.min(newTop, TOTAL_HEIGHT)),
          currentColIndex: colIndex !== null ? colIndex : prev.currentColIndex,
        }));
      });
    };

    const handleTouchEnd = async (e) => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);

      // Use ref instead of state to avoid stale closure —
      // bookings state may have been updated since drag started
      const booking = bookingsRef.current.find((b) => b.id === drag.bookingId);
      if (!booking) {
        setDrag(null);
        return;
      }

      const duration = booking.ends_at - booking.starts_at;

      // Snap the drop position to 15-min increments
      const snappedTop =
        Math.round(drag.currentTop / (SNAP_MINUTES * PX_PER_MIN)) *
        (SNAP_MINUTES * PX_PER_MIN);
      const newStart = pxToDate(snappedTop, currentDate);
      const newEnd = new Date(newStart.getTime() + duration);

      // Clamp within working hours
      if (
        newStart.getHours() < HOUR_START ||
        newEnd.getHours() > HOUR_END ||
        (newEnd.getHours() === HOUR_END && newEnd.getMinutes() > 0)
      ) {
        setDrag(null);
        showToast("Outside working hours", "error");
        return;
      }

      // Get the target barber from the column index
      const targetBarber =
        visibleBarbers[drag.currentColIndex] || booking.barber;

      const barberChanged =
        String(targetBarber.user_id) !== String(booking.barber.user_id);

      // Optimistically update UI
      setBookings((prev) =>
        prev.map((b) =>
          b.id === drag.bookingId
            ? {
                ...b,
                starts_at: newStart,
                ends_at: newEnd,
                barber: targetBarber,
              }
            : b,
        ),
      );
      setDrag(null);

      // Persist to API
      try {
        const body = {
          booking_id: drag.bookingId,
          starts_at: toShopUtc(newStart),
          ends_at: toShopUtc(newEnd),
        };

        if (barberChanged) {
          body.barber_user_id = targetBarber.user_id;
        }

        const response = await api("/tenants/appointments.php", {
          method: "PUT",
          body: JSON.stringify(body),
        });

        if (response?.meta?.code === 200) {
          showToast(
            `Moved to ${dayjs(newStart).format("h:mm A")}${barberChanged ? ` · ${targetBarber.first_name}` : ""}`,
          );
          fetchBookings();
        }
      } catch (err) {
        // Show the specific API error (e.g. "This barber is not working on Monday")
        const message =
          err?.message || err?.error?.message || "Failed to reschedule";
        showToast(message, "error");
        fetchBookings(); // revert optimistic update
      }
    };

    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd, { passive: false });
    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    drag,
    currentDate,
    visibleBarbers,
    getColumnIndexAtX,
    api,
    fetchBookings,
    toShopUtc,
  ]);

  // ── Resize handlers ──────────────────────────────────────────────────

  const handleResizeStart = useCallback(
    (bookingId, clientY, originalEndsAt) => {
      resizeRef.current = {
        bookingId,
        startClientY: clientY,
        originalEndsAt: new Date(originalEndsAt),
        currentEndsAt: new Date(originalEndsAt),
      };
      setResizingId(bookingId);
    },
    [],
  );

  useEffect(() => {
    if (!resizingId) return;

    const handleTouchMove = (e) => {
      e.preventDefault();
      const ref = resizeRef.current;
      if (!ref) return;

      const touch = e.touches[0];
      const deltaY = touch.clientY - ref.startClientY;
      // Snap delta to 15-min increments
      const snappedMs =
        Math.round(deltaY / (SNAP_MINUTES * PX_PER_MIN)) * SNAP_MINUTES * 60000;

      const booking = bookingsRef.current.find((b) => b.id === ref.bookingId);
      if (!booking) return;

      const minEnd = new Date(
        booking.starts_at.getTime() + SNAP_MINUTES * 60000,
      );
      const maxEnd = new Date(booking.starts_at);
      maxEnd.setHours(HOUR_END, 0, 0, 0);

      const newEnd = new Date(
        Math.max(
          minEnd.getTime(),
          Math.min(ref.originalEndsAt.getTime() + snappedMs, maxEnd.getTime()),
        ),
      );

      ref.currentEndsAt = newEnd;

      setBookings((prev) =>
        prev.map((b) =>
          b.id === ref.bookingId ? { ...b, ends_at: newEnd } : b,
        ),
      );
    };

    const handleTouchEnd = async () => {
      const ref = resizeRef.current;
      setResizingId(null);
      resizeRef.current = null;
      if (!ref) return;

      const booking = bookingsRef.current.find((b) => b.id === ref.bookingId);
      if (!booking) return;

      try {
        const response = await api("/tenants/appointments.php", {
          method: "PUT",
          body: JSON.stringify({
            booking_id: ref.bookingId,
            starts_at: toShopUtc(booking.starts_at),
            ends_at: toShopUtc(ref.currentEndsAt),
          }),
        });

        if (response?.meta?.code === 200) {
          const durationMin = Math.round(
            (ref.currentEndsAt - booking.starts_at) / 60000,
          );
          showToast(`Duration updated to ${durationMin} min`);
          fetchBookings();
        }
      } catch (err) {
        showToast(err?.message || "Failed to update duration", "error");
        fetchBookings();
      }
    };

    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [resizingId, api, fetchBookings, toShopUtc]);

  // Auto-scroll while dragging near edges
  useEffect(() => {
    if (!drag) return;
    const grid = gridRef.current;
    if (!grid) return;

    const EDGE_ZONE = 80; // px from edge to trigger scroll
    const SCROLL_SPEED = 6;

    const interval = setInterval(() => {
      const rect = grid.getBoundingClientRect();
      const ghostX = drag.ghostX;
      const ghostY = drag.ghostY;

      const fromTop = ghostY - rect.top;
      const fromBot = rect.bottom - ghostY;
      const fromLeft = ghostX - rect.left;
      const fromRight = rect.right - ghostX;

      // Vertical
      if (fromTop < EDGE_ZONE) grid.scrollTop -= SCROLL_SPEED;
      else if (fromBot < EDGE_ZONE) grid.scrollTop += SCROLL_SPEED;

      // Horizontal
      if (fromLeft < EDGE_ZONE) grid.scrollLeft -= SCROLL_SPEED;
      else if (fromRight < EDGE_ZONE) grid.scrollLeft += SCROLL_SPEED;
    }, 16);

    return () => clearInterval(interval);
  }, [drag]);

  // ── Event handlers ───────────────────────────────────────────────────

  const handleTap = useCallback(
    (booking) => {
      setSelectedBooking(booking);
      fetchBookings(); // silently refresh — useEffect syncs selectedBooking when done
    },
    [fetchBookings],
  );

  const handleStatusChange = async (newStatus) => {
    setStatusLoading(newStatus);
    setDrawerMessage(null);

    const bookingId = selectedBooking.id;

    try {
      if (newStatus === "cancelled") {
        await api("/stripe/cancel-booking.php", {
          method: "POST",
          body: JSON.stringify({ booking_id: bookingId }),
        });
        setDrawerMessage({
          message: "Booking cancelled — refund issued if paid by card.",
          severity: "success",
        });
      } else if (newStatus === "no_show") {
        await api("/stripe/charge-no-show.php", {
          method: "POST",
          body: JSON.stringify({ booking_id: bookingId }),
        });
        setDrawerMessage({
          message: "Booking marked as no-show.",
          severity: "success",
        });
      } else {
        await api("/tenants/appointments.php", {
          method: "PUT",
          body: JSON.stringify({ booking_id: bookingId, status: newStatus }),
        });
        setDrawerMessage({
          message: `Marked as ${STATUS_STYLES[newStatus]?.label.toLowerCase()}.`,
          severity: "success",
        });
      }

      await fetchBookings();
    } catch (err) {
      setDrawerMessage({
        message: err?.message || "Failed to update booking.",
        severity: "error",
      });
      await fetchBookings();
    } finally {
      setStatusLoading(null);
    }
  };

  const showToast = (message, severity = "success") =>
    setToast({ open: true, message, severity });

  const toggleBarber = (id) => {
    setSelectedBarberIds((prev) =>
      prev.length === 0
        ? [id]
        : prev.includes(id)
          ? prev.filter((x) => x !== id)
          : [...prev, id],
    );
  };

  const isAllSelected = selectedBarberIds.length === 0;

  // Column width is calculated dynamically from screen width
  const COLUMN_WIDTH = useMemo(() => {
    const screenW = window.innerWidth;
    const available = screenW - GUTTER_WIDTH;
    const cols = visibleBarbers.length;
    // Show at most 1.8 columns so users know they can scroll horizontally
    const maxVisible = 1.8;
    return Math.max(120, available / Math.min(cols, maxVisible));
  }, [visibleBarbers.length]);

  const totalGridWidth = COLUMN_WIDTH * visibleBarbers.length;

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        overflow: "hidden",
      }}
    >
      {/* ── Header ───────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1,
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
          backgroundColor: "background.default",
        }}
      >
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Appointments
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Manage appointments and track status.
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 0.5,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => setBlockDrawerOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: 12,
                borderColor: "#e5e5e5",
                color: "#6b6b6b",
                "&:hover": { borderColor: "#1a1a1a", color: "#1a1a1a" },
              }}
            >
              Exceptions
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{
                backgroundColor: "#9c6a36",
                textTransform: "none",
                fontWeight: 600,
              }}
              onClick={() => {
                setNewBookingPrefill(null);
                setCreateDialogOpen(true);
              }}
            >
              New Appt
            </Button>
          </Box>
        </Box>

        {/* Barber pills — hidden when calendar is locked to a single barber */}
        {!lockedBarber && (
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              overflowX: "auto",
              pb: 0.5,
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Chip
              label="All"
              onClick={() => setSelectedBarberIds([])}
              size="small"
              sx={{
                flexShrink: 0,
                backgroundColor: isAllSelected ? "#1a1a1a" : "white",
                color: isAllSelected ? "white" : "#1a1a1a",
                border: "1px solid",
                borderColor: isAllSelected ? "#1a1a1a" : "#e5e5e5",
                fontWeight: 500,
                fontSize: 12,
                height: 28,
              }}
            />
            {barbers.map((barber) => {
              const sel =
                !isAllSelected && selectedBarberIds.includes(barber.user_id);
              return (
                <Chip
                  key={barber.user_id}
                  size="small"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          backgroundColor: barber.color.dot,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ whiteSpace: "nowrap" }}>
                        {barber.first_name} {barber.last_name}
                      </span>
                    </Box>
                  }
                  onClick={() => toggleBarber(barber.user_id)}
                  sx={{
                    flexShrink: 0,
                    backgroundColor: sel ? barber.color.bg : "white",
                    border: `1px solid ${sel ? barber.color.border : "#e5e5e5"}`,
                    color: sel ? barber.color.text : "#1a1a1a",
                    fontWeight: 500,
                    fontSize: 12,
                    height: 28,
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              );
            })}
          </Box>
        )}
      </Box>

      {/* ── Week strip ───────────────────────────────────────────── */}
      <Box sx={{ flexShrink: 0, borderBottom: "1px solid #f0f0f0" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            px: 1,
            py: 1,
            width: "100%",
          }}
        >
          <IconButton
            size="small"
            onClick={() =>
              setCurrentDate(dayjs(currentDate).subtract(7, "day").toDate())
            }
            sx={{ color: "#6b6b6b" }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Box sx={{ display: "flex", flex: 1, width: 0 }}>
            {weekDays.map((day) => {
              const isSel = day.isSame(currentDate, "day");
              const isToday = day.isSame(new Date(), "day");
              const count = weekCounts[day.format("YYYY-MM-DD")] || 0;
              return (
                <Box
                  key={day.toString()}
                  onClick={() => setCurrentDate(day.toDate())}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    py: 1,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    textAlign: "center",
                    backgroundColor: isSel ? "#1a1a1a" : "transparent",
                    position: "relative",
                  }}
                >
                  {isToday && !isSel && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 8,
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        backgroundColor: "#9c6a36",
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: 9,
                      color: isSel ? "rgba(255,255,255,0.6)" : "#9b9b9b",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      fontWeight: 500,
                    }}
                  >
                    {day.format("ddd")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: isSel ? "white" : "#1a1a1a",
                      lineHeight: 1.2,
                    }}
                  >
                    {day.format("D")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 9,
                      color: isSel ? "rgba(255,255,255,0.6)" : "#9b9b9b",
                    }}
                  >
                    {count > 0 ? `${count}` : "–"}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <IconButton
            size="small"
            onClick={() =>
              setCurrentDate(dayjs(currentDate).add(7, "day").toDate())
            }
            sx={{ color: "#6b6b6b" }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* ── Long press hint ───────────────────────────────────────── */}
      <Box
        sx={{
          px: 2,
          py: 0.75,
          backgroundColor: "#fafafa",
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{ fontSize: 11, color: "#9b9b9b", textAlign: "center" }}
        >
          Long press to drag · Tap to view details · Scroll left/right for more
          barbers
        </Typography>
      </Box>

      {/* ── Main grid — single scroll container (barber header lives inside) ── */}
      <Box
        ref={gridRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "auto",
          position: "relative",
          touchAction: drag || resizingId ? "none" : "pan-y pan-x",
          "&::-webkit-scrollbar": { width: 4, height: 4 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#e0e0e0",
            borderRadius: 2,
          },
        }}
      >
        {/* ── Sticky barber header row — scrolls with grid horizontally, sticks vertically ── */}
        <Box
          sx={{
            display: "flex",
            width: totalGridWidth + GUTTER_WIDTH,
            minWidth: "100%",
            position: "sticky",
            top: 0,
            zIndex: 10,
            borderBottom: "1px solid #f0f0f0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
          }}
        >
          <Box
            sx={{
              width: GUTTER_WIDTH,
              flexShrink: 0,
              backgroundColor: "#fafafa",
              borderRight: "1px solid #f0f0f0",
            }}
          />
          {visibleBarbers.map((barber, i) => (
            <Box
              key={barber.user_id}
              ref={(el) => (columnRefs.current[i] = el)}
              sx={{
                width: COLUMN_WIDTH,
                flexShrink: 0,
                backgroundColor: barber.color.soft,
                borderTop: `3px solid ${barber.color.border}`,
                borderLeft: i > 0 ? "1px solid #f0f0f0" : "none",
                py: 1,
                px: 1,
                textAlign: "center",
                overflow: "hidden",
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: barber.color.text,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {barber.first_name} {barber.last_name}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* ── Time gutter + barber columns ── */}
        <Box
          sx={{
            display: "flex",
            width: totalGridWidth + GUTTER_WIDTH,
            minWidth: "100%",
          }}
        >
          <TimeGutter />
          <Box
            sx={{
              position: "relative",
              flex: 1,
              display: "flex",
              height: TOTAL_HEIGHT,
            }}
          >
            {drag && drag.currentColIndex !== null && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: drag.currentColIndex * COLUMN_WIDTH,
                  width: COLUMN_WIDTH,
                  height: TOTAL_HEIGHT,
                  backgroundColor: "rgba(156,106,54,0.06)",
                  borderLeft: "2px solid rgba(156,106,54,0.3)",
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              />
            )}

            {drag && (
              <Box
                sx={{
                  position: "absolute",
                  top:
                    Math.round(drag.currentTop / (SNAP_MINUTES * PX_PER_MIN)) *
                    (SNAP_MINUTES * PX_PER_MIN),
                  left:
                    drag.currentColIndex !== null
                      ? drag.currentColIndex * COLUMN_WIDTH
                      : 0,
                  width: COLUMN_WIDTH,
                  height: 2,
                  backgroundColor: "#9c6a36",
                  opacity: 0.6,
                  pointerEvents: "none",
                  zIndex: 3,
                }}
              />
            )}

            {visibleBarbers.map((barber, colIndex) => {
              const colBookings = bookings.filter(
                (b) =>
                  b.barber.user_id === barber.user_id &&
                  dayjs(b.starts_at).isSame(currentDate, "day"),
              );
              return (
                <Box
                  key={barber.user_id}
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      const grid = gridRef.current;
                      if (!grid) return;
                      const rect = grid.getBoundingClientRect();
                      const relY = e.clientY - rect.top + grid.scrollTop;
                      const snappedPx =
                        Math.round(relY / (SNAP_MINUTES * PX_PER_MIN)) *
                        (SNAP_MINUTES * PX_PER_MIN);
                      const tappedTime = pxToDate(snappedPx, currentDate);
                      setNewBookingPrefill({
                        barber,
                        date: dayjs(currentDate).format("YYYY-MM-DD"),
                        slot: {
                          start: dayjs(tappedTime).format("h:mm A"),
                          start_utc: toShopUtc(tappedTime),
                        },
                      });
                      setCreateDialogOpen(true);
                    }
                  }}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: colIndex * COLUMN_WIDTH,
                    width: COLUMN_WIDTH,
                    height: TOTAL_HEIGHT,
                    borderLeft: colIndex > 0 ? "1px solid #f0f0f0" : "none",
                  }}
                >
                  <GridLines />
                  <NowIndicator currentDate={currentDate} />

                  {/* Gray out non-working hours for this barber */}
                  {(() => {
                    const overlayStyle = {
                      position: "absolute",
                      left: 0,
                      right: 0,
                      backgroundColor: "rgba(0,0,0,0.04)",
                      backgroundImage:
                        "repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.025) 4px, rgba(0,0,0,0.025) 8px)",
                      zIndex: 0,
                      pointerEvents: "none",
                    };

                    // Date override takes priority over regular weekly schedule
                    const override = dateOverrides[barber.user_id];
                    let effectiveStartTime, effectiveEndTime;

                    if (override) {
                      effectiveStartTime = override.start_time;
                      effectiveEndTime = override.end_time;
                    } else {
                      const hours = barberHours[barber.user_id];
                      if (!hours) return null;
                      const dayOfWeek = dayjs(currentDate).day();
                      const todayHours = hours.find(
                        (h) => h.day_of_week === dayOfWeek,
                      );
                      if (!todayHours || todayHours.is_working === 0) {
                        return (
                          <Box
                            sx={{
                              ...overlayStyle,
                              top: 0,
                              height: TOTAL_HEIGHT,
                            }}
                          />
                        );
                      }
                      effectiveStartTime = todayHours.start_time;
                      effectiveEndTime = todayHours.end_time;
                    }

                    const [startH, startM] = effectiveStartTime
                      .split(":")
                      .map(Number);
                    const [endH, endM] = effectiveEndTime
                      .split(":")
                      .map(Number);
                    const workStartPx =
                      ((startH - HOUR_START) * 60 + startM) * PX_PER_MIN;
                    const workEndPx =
                      ((endH - HOUR_START) * 60 + endM) * PX_PER_MIN;
                    return (
                      <>
                        {workStartPx > 0 && (
                          <Box
                            sx={{
                              ...overlayStyle,
                              top: 0,
                              height: workStartPx,
                            }}
                          />
                        )}
                        {workEndPx < TOTAL_HEIGHT && (
                          <Box
                            sx={{
                              ...overlayStyle,
                              top: workEndPx,
                              height: TOTAL_HEIGHT - workEndPx,
                            }}
                          />
                        )}
                      </>
                    );
                  })()}
                  {(() => {
                    // Sort so cancelled/completed/no_show always render first
                    // (lowest stackIndex = behind), active bookings render last (on top)
                    const STATUS_ORDER = {
                      cancelled: 0,
                      no_show: 1,
                      completed: 2,
                      pending: 3,
                      confirmed: 4,
                    };
                    const sorted = [...colBookings].sort(
                      (a, b) =>
                        (STATUS_ORDER[a.status] ?? 3) -
                        (STATUS_ORDER[b.status] ?? 3),
                    );

                    return sorted.map((booking, i) => {
                      // Find how many bookings this one overlaps with
                      const overlapping = sorted.filter(
                        (other, j) =>
                          j !== i &&
                          booking.starts_at < other.ends_at &&
                          booking.ends_at > other.starts_at,
                      );

                      // stackIndex based on sorted position among overlapping events
                      const stackIndex = sorted
                        .slice(0, i)
                        .filter(
                          (other) =>
                            booking.starts_at < other.ends_at &&
                            booking.ends_at > other.starts_at,
                        ).length;

                      const stackTotal = overlapping.length + 1;

                      return (
                        <EventBlock
                          key={booking.id}
                          booking={booking}
                          columnWidth={COLUMN_WIDTH}
                          colIndex={colIndex}
                          onDragStart={handleDragStart}
                          onResizeStart={handleResizeStart}
                          onTap={handleTap}
                          isDragging={drag?.bookingId}
                          isResizing={!!resizingId}
                          dragY={drag?.currentTop}
                          dragColIndex={drag?.currentColIndex}
                          stackIndex={stackIndex}
                          stackTotal={stackTotal}
                          liveDragStart={
                            drag?.bookingId === booking.id
                              ? pxToDate(
                                  Math.round(
                                    drag.currentTop /
                                      (SNAP_MINUTES * PX_PER_MIN),
                                  ) *
                                    (SNAP_MINUTES * PX_PER_MIN),
                                  currentDate,
                                )
                              : null
                          }
                        />
                      );
                    });
                  })()}

                  {/* Block events for this barber column */}
                  {blockEvents
                    .filter((b) => b.barber_user_id === barber.user_id)
                    .map((block) => {
                      const top = dateToPx(block.starts_at);
                      const height = Math.max(
                        ((block.ends_at - block.starts_at) / 60000) *
                          PX_PER_MIN,
                        28,
                      );
                      const label =
                        block.blockData?.notes &&
                        block.blockData?.reason === "other"
                          ? block.blockData.notes
                          : {
                              lunch: "🍽 Lunch",
                              break: "☕ Break",
                              vacation: "🏖 Vacation",
                              sick_day: "🤒 Sick day",
                              personal: "Personal",
                              other: "Blocked",
                            }[block.blockData?.reason] || "Blocked";
                      return (
                        <Box
                          key={block.id}
                          onTouchEnd={() => setSelectedBlock(block.blockData)}
                          sx={{
                            position: "absolute",
                            top,
                            left: 2,
                            right: 2,
                            height,
                            backgroundColor: "#f0ece8",
                            borderLeft: "3px solid #c0bbb4",
                            borderRadius: "4px",
                            px: "6px",
                            py: "3px",
                            overflow: "hidden",
                            backgroundImage:
                              "repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(0,0,0,0.025) 5px, rgba(0,0,0,0.025) 10px)",
                            zIndex: 10,
                            cursor: "pointer",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <BlockIcon
                              sx={{ fontSize: 10, color: "#9b9b9b" }}
                            />
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: "#6b6b6b",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {label}
                              {block.blockData?.is_recurring ? " 🔁" : ""}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ── Drag ghost — rendered outside scroll container ────────── */}
      {drag && draggingBooking && (
        <DragGhost
          booking={draggingBooking}
          x={drag.ghostX}
          y={drag.ghostY}
          columnWidth={COLUMN_WIDTH}
          liveDragStart={
            drag
              ? pxToDate(
                  Math.round(drag.currentTop / (SNAP_MINUTES * PX_PER_MIN)) *
                    (SNAP_MINUTES * PX_PER_MIN),
                  currentDate,
                )
              : null
          }
        />
      )}

      {/* ── Booking detail drawer ────────────────────────────────── */}
      <Drawer
        anchor="bottom"
        open={!!selectedBooking}
        onClose={() => {
          setSelectedBooking(null);
          setDrawerMessage(null);
        }}
        PaperProps={{
          sx: { borderRadius: "16px 16px 0 0", maxHeight: "80dvh" },
        }}
      >
        {selectedBooking && (
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {/* Pull handle */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pt: 1.5,
                pb: 0.5,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: "#e0e0e0",
                }}
              />
            </Box>

            {/* Header */}
            <Box
              sx={{
                px: 2.5,
                pb: 1.5,
                borderBottom: "1px solid #f0f0f0",
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "baseline",
                    gap: 0.75,
                  }}
                >
                  <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
                    Booking
                  </Typography>
                  <Typography sx={{ fontSize: 17, color: "#9b9b9b" }}>
                    #{selectedBooking.id}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedBooking(null);
                    setDrawerMessage(null);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Chip
                label={STATUS_STYLES[selectedBooking.status]?.label}
                size="small"
                sx={{
                  mt: 1,
                  backgroundColor: STATUS_STYLES[selectedBooking.status]?.bg,
                  color: STATUS_STYLES[selectedBooking.status]?.text,
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                }}
              />
            </Box>

            {/* Body */}
            <Box sx={{ px: 2.5, py: 2, flex: 1, overflowY: "auto" }}>
              {[
                {
                  icon: <PersonIcon sx={{ fontSize: 14, color: "#9b9b9b" }} />,
                  label: "Client",
                  content: (
                    <>
                      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                        {selectedBooking.client.first_name}{" "}
                        {selectedBooking.client.last_name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.25,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <EmailIcon sx={{ fontSize: 12, color: "#9b9b9b" }} />
                          <Typography sx={{ fontSize: 13, color: "#6b6b6b" }}>
                            {selectedBooking.client.email}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <PhoneIcon sx={{ fontSize: 12, color: "#9b9b9b" }} />
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                          >
                            <a
                              href={`tel:${selectedBooking.client.phone}`}
                              style={{
                                color: "inherit",
                                textDecoration: "none",
                              }}
                            >
                              {formatPhoneNumber(selectedBooking.client.phone)}
                            </a>
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  ),
                },
                {
                  icon: (
                    <AccountCircleIcon
                      sx={{ fontSize: 14, color: "#9b9b9b" }}
                    />
                  ),
                  label: "Barber",
                  content: (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: selectedBooking.barber.color.dot,
                        }}
                      />
                      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                        {selectedBooking.barber.first_name}{" "}
                        {selectedBooking.barber.last_name}
                      </Typography>
                    </Box>
                  ),
                },
                {
                  icon: (
                    <ContentCutIcon sx={{ fontSize: 14, color: "#9b9b9b" }} />
                  ),
                  label: "Service",
                  content: (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                        {selectedBooking.service.name}
                      </Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                        ${selectedBooking.price_charged.toFixed(2)}
                      </Typography>
                    </Box>
                  ),
                },
                {
                  icon: (
                    <PaymentsIcon sx={{ fontSize: 14, color: "#9b9b9b" }} />
                  ),
                  label: "Payment",
                  content: (() => {
                    const b = selectedBooking;
                    const pm = b.payment_method;
                    const ps = b.payment_status;
                    const paid = b.amount_paid;

                    const methodLabel =
                      (["cancelled", "no_show"].includes(b.status) &&
                        pm === "cash" &&
                        ps === "paid") ||
                      pm === "card"
                        ? "💳 Card"
                        : "💵 Cash";

                    let statusLabel = ps;
                    if (
                      b.status === "cancelled" &&
                      pm === "card" &&
                      ps === "refunded"
                    )
                      statusLabel = "Refunded";
                    else if (
                      b.status === "cancelled" &&
                      pm === "card" &&
                      ps === "paid"
                    )
                      statusLabel = "Non-refundable";
                    else if (
                      b.status === "cancelled" &&
                      pm === "cash" &&
                      ps === "unpaid"
                    )
                      statusLabel = "Cancelled";
                    else if (
                      b.status === "cancelled" &&
                      pm === "cash" &&
                      ps === "paid"
                    )
                      statusLabel = "Late cancellation fee";
                    else if (
                      b.status === "no_show" &&
                      pm === "card" &&
                      ps === "paid"
                    )
                      statusLabel = "Paid at booking";
                    else if (
                      b.status === "no_show" &&
                      pm === "cash" &&
                      ps === "paid"
                    )
                      statusLabel = "No-show fee charged";
                    else if (ps === "unpaid")
                      statusLabel = "Pay at appointment";
                    else if (ps === "paid") statusLabel = "Paid";

                    let amountLabel = `$${b.price_charged.toFixed(2)}`;
                    if (
                      ["cancelled", "no_show"].includes(b.status) &&
                      pm === "cash" &&
                      ps === "paid" &&
                      paid
                    ) {
                      amountLabel = `$${Number(paid).toFixed(2)} charged`;
                    } else if (pm === "card" && ps === "paid" && paid) {
                      amountLabel = `$${Number(paid).toFixed(2)}`;
                    }

                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 14, fontWeight: 600, mr: 0.5 }}
                        >
                          {amountLabel}
                        </Typography>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            border: "1px solid #e5e5e5",
                            fontSize: 11,
                            fontWeight: 500,
                            color: "#4b4b4b",
                            backgroundColor: "#fafafa",
                          }}
                        >
                          {methodLabel}
                        </Box>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: 11,
                            fontWeight: 500,
                            backgroundColor: [
                              "Non-refundable",
                              "Late cancellation fee",
                              "No-show fee charged",
                            ].includes(statusLabel)
                              ? "#FCEBEB"
                              : statusLabel === "Refunded"
                                ? "#E6F1FB"
                                : "#f0f0f0",
                            color: [
                              "Non-refundable",
                              "Late cancellation fee",
                              "No-show fee charged",
                            ].includes(statusLabel)
                              ? "#A32D2D"
                              : statusLabel === "Refunded"
                                ? "#0C447C"
                                : "#4b4b4b",
                          }}
                        >
                          {statusLabel}
                        </Box>
                      </Box>
                    );
                  })(),
                },
                {
                  icon: (
                    <AccessTimeIcon sx={{ fontSize: 14, color: "#9b9b9b" }} />
                  ),
                  label: "Time",
                  content: (
                    <>
                      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                        {dayjs(selectedBooking.starts_at).format(
                          "dddd, MMMM D",
                        )}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 13, color: "#6b6b6b", mt: 0.25 }}
                      >
                        {dayjs(selectedBooking.starts_at).format("h:mm A")} →{" "}
                        {dayjs(selectedBooking.ends_at).format("h:mm A")}
                        {" · "}
                        {Math.round(
                          (selectedBooking.ends_at -
                            selectedBooking.starts_at) /
                            60000,
                        )}{" "}
                        min
                      </Typography>
                    </>
                  ),
                },
              ].map(({ icon, label, content }) => (
                <Box key={label} sx={{ mb: 2.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 0.75,
                    }}
                  >
                    {icon}
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "#9b9b9b",
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        fontWeight: 600,
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  {content}
                </Box>
              ))}
              {selectedBooking.notes && (
                <Box sx={{ mb: 2.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 0.75,
                    }}
                  >
                    <NotesIcon sx={{ fontSize: 14, color: "#9b9b9b" }} />
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "#9b9b9b",
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        fontWeight: 600,
                      }}
                    >
                      Notes
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "#4b4b4b",
                      fontStyle: "italic",
                      lineHeight: 1.6,
                    }}
                  >
                    {selectedBooking.notes}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Actions */}
            <Box
              sx={{
                px: 2.5,
                py: 2,
                borderTop: "1px solid #f0f0f0",
                backgroundColor: "#fafafa",
                flexShrink: 0,
              }}
            >
              {/* ── Inline status message ── */}
              {drawerMessage && (
                <Alert
                  severity={drawerMessage.severity}
                  onClose={() => setDrawerMessage(null)}
                  sx={{ mb: 1.5, fontSize: 12, borderRadius: 1.5 }}
                >
                  {drawerMessage.message}
                </Alert>
              )}
              {["completed", "cancelled", "no_show"].includes(
                selectedBooking.status,
              ) ? (
                <Typography
                  sx={{ fontSize: 12, color: "#9b9b9b", textAlign: "center" }}
                >
                  This booking is{" "}
                  {selectedBooking.status === "no_show"
                    ? "marked as no-show"
                    : selectedBooking.status}
                  . No further actions.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {selectedBooking.status === "pending" && (
                    <Button
                      startIcon={
                        statusLoading === "confirmed" ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CheckCircleIcon sx={{ fontSize: 18 }} />
                        )
                      }
                      onClick={() => handleStatusChange("confirmed")}
                      disabled={!!statusLoading}
                      fullWidth
                      sx={{
                        backgroundColor: "#1a1a1a",
                        color: "white",
                        textTransform: "none",
                        fontWeight: 500,
                        py: 1.25,
                        borderRadius: 1.5,
                        "&:hover": { backgroundColor: "#000" },
                        "&.Mui-disabled": {
                          backgroundColor: "#1a1a1a",
                          color: "rgba(255,255,255,0.5)",
                        },
                      }}
                    >
                      Confirm Booking
                    </Button>
                  )}
                  <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                    {[
                      {
                        label: "Complete",
                        icon: <DoneAllIcon sx={{ fontSize: 16 }} />,
                        status: "completed",
                        color: "#0F6E56",
                        hoverBg: "#f0f9f5",
                      },
                      {
                        label: "No-show",
                        icon: <EventBusyIcon sx={{ fontSize: 16 }} />,
                        status: "no_show",
                        color: "#854F0B",
                        hoverBg: "#fdf8ef",
                      },
                    ].map((btn) => (
                      <Button
                        key={btn.status}
                        startIcon={
                          statusLoading === btn.status ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            btn.icon
                          )
                        }
                        onClick={() => handleStatusChange(btn.status)}
                        disabled={!!statusLoading}
                        sx={{
                          flex: 1,
                          backgroundColor: "white",
                          color: btn.color,
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: 13,
                          py: 1,
                          borderRadius: 1.5,
                          border: "1px solid #e5e5e5",
                          "&:hover": {
                            backgroundColor: btn.hoverBg,
                            borderColor: btn.color,
                          },
                        }}
                      >
                        {btn.label}
                      </Button>
                    ))}
                  </Box>
                  <Button
                    startIcon={
                      statusLoading === "cancelled" ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <CancelIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    onClick={() => handleStatusChange("cancelled")}
                    disabled={!!statusLoading}
                    fullWidth
                    sx={{
                      backgroundColor: "white",
                      color: "#A32D2D",
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: 13,
                      py: 1,
                      borderRadius: 1.5,
                      border: "1px solid #e5e5e5",
                      "&:hover": {
                        backgroundColor: "#fdf6f6",
                        borderColor: "#A32D2D",
                      },
                    }}
                  >
                    Cancel Booking
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* ── New Booking Drawer ────────────────────────────────────── */}
      <NewBookingDrawer
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setNewBookingPrefill(null);
        }}
        barbers={barbers}
        preselectedBarber={newBookingPrefill?.barber || lockedBarber || null}
        preselectedDate={newBookingPrefill?.date || null}
        preselectedSlot={newBookingPrefill?.slot || null}
        onCreated={() => {
          fetchBookings();
          fetchWeekCounts();
          setCreateDialogOpen(false);
          setNewBookingPrefill(null);
        }}
      />

      {/* ── Block drawer ─────────────────────────────────────────── */}
      <BlockDrawer
        open={blockDrawerOpen}
        onClose={() => setBlockDrawerOpen(false)}
        barbers={barbers}
        preselectedDate={dayjs(currentDate).format("YYYY-MM-DD")}
        onCreated={() => {
          setBlockDrawerOpen(false);
          fetchBlocks();
          fetchDateOverrides(barbers);
        }}
      />

      {/* ── Block delete dialog ───────────────────────────────────── */}
      <Dialog
        open={!!selectedBlock}
        onClose={() => !deletingBlock && setSelectedBlock(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove block</DialogTitle>
        <DialogContent>
          {selectedBlock?.is_recurring && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              This is a recurring block. Removing it deletes all future
              occurrences.
            </Alert>
          )}
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Remove the{" "}
            <strong>
              {{
                lunch: "🍽 Lunch",
                break: "☕ Break",
                vacation: "🏖 Vacation",
                sick_day: "🤒 Sick day",
                personal: "Personal",
                other: "block",
              }[selectedBlock?.reason] || "block"}
            </strong>{" "}
            from the calendar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSelectedBlock(null)}
            disabled={deletingBlock}
          >
            Keep
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deletingBlock}
            onClick={async () => {
              setDeletingBlock(true);
              try {
                await api("/barbers/blocks.php", {
                  method: "DELETE",
                  body: JSON.stringify({ block_id: selectedBlock.id }),
                });
                setSelectedBlock(null);
                fetchBlocks();
                showToast("Block removed");
              } catch (err) {
                showToast(err?.message || "Failed to remove block", "error");
              } finally {
                setDeletingBlock(false);
              }
            }}
          >
            {deletingBlock ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              "Remove"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Toast ─────────────────────────────────────────────────── */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ borderRadius: 2, fontWeight: 500 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
